import type { BaseStack } from 'generated/graphql';
import type {
  DehydratedViewportConfiguration,
  DehydratedViewportsConfigurations,
  SlimSeries,
  SlimStudy,
  SlimFrame,
  StudyGroup,
  ViewportConfiguration,
  ViewportsConfigurations,
  SlimStack,
  Stack,
  Series,
  Study,
  Frame,
  FullMultiLayerStack,
} from './types';
import memoize from 'lodash.memoize';
import { decodeOverlayStackSmidToStackLayers, isStackSmidMultiLayer } from '../Viewer/stackUtils';
/**
 * Takes a list of viewport configurations and merges them into a single list.
 * The last occurrence is preserved, the list is NOT deep merged.
 */
export function mergeViewportsConfigurations(
  ...configurations: ReadonlyArray<Nullable<ViewportsConfigurations>>
): ViewportsConfigurations {
  return configurations.reduce<ViewportsConfigurations>((acc, configuration) => {
    return {
      ...acc,
      ...configuration,
    };
  }, {});
}

export function viewportsConfigurationsEntries(
  viewportsConfigurations: ViewportsConfigurations | DehydratedViewportsConfigurations
): [string, Nullable<ViewportConfiguration>][] {
  // @ts-expect-error [EN-7967] - TS2322 - Type '[string, DehydratedViewportConfiguration][]' is not assignable to type '[string, ViewportConfiguration][]'.
  return Object.entries(viewportsConfigurations);
}

export function filterViewportsConfigurations(
  viewportsConfigurations: ViewportsConfigurations | DehydratedViewportsConfigurations,
  filter: (arg1: [string, Nullable<ViewportConfiguration>]) => boolean
): ViewportsConfigurations {
  return Object.fromEntries(viewportsConfigurationsEntries(viewportsConfigurations).filter(filter));
}

// Cache hydrate/dehydrate so unchanged viewports keep identity-stable refs across the round-trip.
const dehydratedByHydrated = new WeakMap<ViewportConfiguration, DehydratedViewportConfiguration>();
type HydrationEntry = {
  groupedStudies: ReadonlyArray<StudyGroup>;
  hydrated: Nullable<ViewportConfiguration>;
};
const hydratedByDehydrated = new WeakMap<DehydratedViewportConfiguration, HydrationEntry>();

// Shared smid -> object lookup for a given groupedStudies array. Hydrating N viewports against
// the same groupedStudies used to do 3*N O(n^2) spread-reduces (one studies/series/stacks set
// per viewport); now all viewports share a single O(studies+series+stacks) build.
type GroupedStudiesLookups = {
  studies: Map<string, SlimStudy>;
  series: Map<string, SlimSeries>;
  stacks: Map<string, SlimStack>;
  // Maps seriesSmid -> first stack whose frames reference that series. Built once per
  // groupedStudies snapshot for the stepped-HP stack fallback in hydrateViewportConfiguration.
  stackBySeriesSmid: Map<string, SlimStack>;
};
const lookupsByGroupedStudies = new WeakMap<ReadonlyArray<StudyGroup>, GroupedStudiesLookups>();
function getGroupedStudiesLookups(
  groupedStudies: ReadonlyArray<StudyGroup>
): GroupedStudiesLookups {
  const cached = lookupsByGroupedStudies.get(groupedStudies);
  if (cached != null) return cached;
  const studies = new Map<string, SlimStudy>();
  const series = new Map<string, SlimSeries>();
  const stacks = new Map<string, SlimStack>();
  const stackBySeriesSmid = new Map<string, SlimStack>();
  for (const group of groupedStudies) {
    for (const study of group) {
      studies.set(study.smid, study);
      for (const s of study.seriesList) series.set(s.smid, s);
      for (const stack of study.stackedFrames) {
        stacks.set(stack.smid, stack);
        for (const frame of stack.frames ?? []) {
          const seriesSmid = frame.series?.smid;
          if (seriesSmid != null && !stackBySeriesSmid.has(seriesSmid)) {
            stackBySeriesSmid.set(seriesSmid, stack);
          }
        }
      }
    }
  }
  const lookups = { studies, series, stacks, stackBySeriesSmid };
  lookupsByGroupedStudies.set(groupedStudies, lookups);
  return lookups;
}

export function dehydrateViewportConfiguration(
  viewportConfiguration: ViewportConfiguration
): DehydratedViewportConfiguration {
  const cached = dehydratedByHydrated.get(viewportConfiguration);
  if (cached != null) return cached;
  const dehydrated: DehydratedViewportConfiguration = {
    ...viewportConfiguration,
    study: viewportConfiguration.study && { smid: viewportConfiguration.study.smid },
    series: viewportConfiguration.series && { smid: viewportConfiguration.series.smid },
    stack: viewportConfiguration.stack && { smid: viewportConfiguration.stack.smid },
  };
  dehydratedByHydrated.set(viewportConfiguration, dehydrated);
  return dehydrated;
}

export function hydrateViewportConfiguration(
  viewportConfiguration: DehydratedViewportConfiguration,
  groupedStudies: ReadonlyArray<StudyGroup>
): Nullable<ViewportConfiguration> {
  const cached = hydratedByDehydrated.get(viewportConfiguration);
  if (cached != null) {
    if (cached.groupedStudies === groupedStudies) return cached.hydrated;
    // groupedStudies array is new but the slim refs inside may be unchanged (chip-click refetch).
    if (cached.hydrated != null) {
      // Truly empty viewport (no study smid): content unaffected by groupedStudies churn.
      if (viewportConfiguration.study == null) {
        hydratedByDehydrated.set(viewportConfiguration, {
          groupedStudies,
          hydrated: cached.hydrated,
        });
        return cached.hydrated;
      }
      // Has a study smid: cached is only safe to reuse when the same slim study ref is still in groupedStudies.
      const cachedStudy = cached.hydrated.study;
      if (cachedStudy != null) {
        for (const group of groupedStudies) {
          for (const study of group) {
            if (study === cachedStudy) {
              hydratedByDehydrated.set(viewportConfiguration, {
                groupedStudies,
                hydrated: cached.hydrated,
              });
              return cached.hydrated;
            }
          }
        }
      }
    }
  }

  const { studies, series, stacks, stackBySeriesSmid } = getGroupedStudiesLookups(groupedStudies);

  // We have a specific study we are looking for here, if it isnt mapped yet return null
  if (
    viewportConfiguration.wasDropped === true &&
    viewportConfiguration.study &&
    studies.get(viewportConfiguration.study.smid) == null
  ) {
    hydratedByDehydrated.set(viewportConfiguration, { groupedStudies, hydrated: null });
    return null;
  }

  const config: ViewportConfiguration = {
    ...viewportConfiguration,
    study: viewportConfiguration.study && studies.get(viewportConfiguration.study.smid),
    series: viewportConfiguration.series && series.get(viewportConfiguration.series.smid),
    stack: null,
  };

  if (
    viewportConfiguration.stack &&
    viewportConfiguration.stack.smid != null &&
    isStackSmidMultiLayer(viewportConfiguration.stack.smid) &&
    viewportConfiguration.study != null &&
    viewportConfiguration.study.smid != null
  ) {
    config.stack = {
      smid: viewportConfiguration.stack.smid,
      type: '',
      study: {
        smid: viewportConfiguration.study.smid,
      },
    };
  } else {
    config.stack = viewportConfiguration.stack && stacks.get(viewportConfiguration.stack.smid);
  }

  // Best-effort stack fallback for SlimStack data (e.g. stepped HPs where
  // /hang-cases returns series but not stacks). SlimStack.frames carries series.smid,
  // so we recover the stack via the seriesSmid -> stack index built once per
  // groupedStudies snapshot (see getGroupedStudiesLookups), mirroring the graphql
  // hang-cases resolver's matching logic. The primary stack fallback is in
  // ViewerRecoilContext's getStoreValues(), which has full Study objects.
  if (config.stack == null && config.series != null) {
    const matchedStack = stackBySeriesSmid.get(config.series.smid);
    if (matchedStack != null) {
      config.stack = matchedStack;
    }
  }

  hydratedByDehydrated.set(viewportConfiguration, { groupedStudies, hydrated: config });
  // Seed the reverse map so dehydrate(config) round-trips back to the same dehydrated ref.
  dehydratedByHydrated.set(config, viewportConfiguration);
  return config;
}

export function dehydrateViewportsConfigurations(
  viewportsConfigurations: ViewportsConfigurations
): DehydratedViewportsConfigurations {
  return Object.fromEntries(
    viewportsConfigurationsEntries(viewportsConfigurations).map(
      ([key, viewportConfiguration]: [any, any]) => [
        key,
        viewportConfiguration != null
          ? dehydrateViewportConfiguration(viewportConfiguration)
          : null,
      ]
    )
  );
}

export function hydrateViewportsConfigurations(
  viewportsConfigurations: DehydratedViewportsConfigurations,
  groupedStudies: StudyGroup[]
): ViewportsConfigurations {
  return Object.fromEntries(
    Object.entries(viewportsConfigurations).map(([key, viewportConfiguration]: [any, any]) => [
      key,
      viewportConfiguration != null
        ? hydrateViewportConfiguration(viewportConfiguration, groupedStudies)
        : null,
    ])
  );
}

// Cache slim conversions by source ref so successive Apollo responses with the same entity refs produce identity-stable slim outputs.
const slimStudyCache = new WeakMap<Study, SlimStudy>();
const slimSeriesCache = new WeakMap<Series, SlimSeries>();
const slimStackCache = new WeakMap<Stack | BaseStack, SlimStack>();

export function studyToSlimStudy(study: Study): SlimStudy {
  const cached = slimStudyCache.get(study);
  if (cached != null) return cached;
  const slim: SlimStudy = {
    smid: study.smid,
    description: study.description,
    seriesList: study.seriesList.map((series) => seriesToSlimSeries(series)),
    stackedFrames: study.stackedFrames.map((stack) => stackToSlimStack(stack)),
  };
  slimStudyCache.set(study, slim);
  return slim;
}

export function seriesToSlimSeries(series: Series): SlimSeries {
  const cached = slimSeriesCache.get(series);
  if (cached != null) return cached;
  const slim: SlimSeries = {
    smid: series.smid,
    description: series.description,
    study: { smid: series.study.smid },
  };
  slimSeriesCache.set(series, slim);
  return slim;
}

export function stackToSlimStack(stack: Stack | BaseStack): SlimStack {
  const cached = slimStackCache.get(stack);
  if (cached != null) return cached;
  const slim: SlimStack = {
    smid: stack.smid,
    type: stack.type,
    study: { smid: stack.study.smid },
  };

  // @ts-expect-error [EN-7967] - TS2339 - Property 'frames' does not exist on type 'BaseStack | Stack'.
  if (stack.frames != null) {
    slim.frames =
      // @ts-expect-error [EN-7967] - TS2339 - Property 'frames' does not exist on type 'BaseStack | Stack'.
      stack.frames.length > 0
        ? [
            {
              // @ts-expect-error [EN-7967] - TS2339 - Property 'frames' does not exist on type 'BaseStack | Stack'.
              smid: stack.frames[0].smid,
              // @ts-expect-error [EN-7967] - TS2339 - Property 'frames' does not exist on type 'BaseStack | Stack'.
              series: { smid: stack.frames[0].series.smid },
              // @ts-expect-error [EN-7967] - TS2339 - Property 'frames' does not exist on type 'BaseStack | Stack'.
              sopClassUID: stack.frames[0].sopClassUID,
            },
          ]
        : [];
  }

  slimStackCache.set(stack, slim);
  return slim;
}

export function frameToSlimFrame(frame: Frame): SlimFrame {
  return {
    smid: frame.smid,
    series: { smid: frame.series.smid },
    sopClassUID: '123',
  };
}

export const rehydrateSlimStackToLayeredStack: (
  slimStack?: Nullable<SlimStack>,
  study?: Nullable<Study>
) => Nullable<FullMultiLayerStack> = memoize(
  (slimStack?: SlimStack | null, study?: Study | null): Nullable<FullMultiLayerStack> => {
    if (study != null && slimStack != null && isStackSmidMultiLayer(slimStack.smid)) {
      const stack: FullMultiLayerStack = {
        __typename: 'LayeredStack',
        type: '',
        smid: slimStack.smid,
        stackLayers: decodeOverlayStackSmidToStackLayers(slimStack.smid),
        study: { smid: study.smid },
      };

      return stack;
    }
    return null;
  },
  (slimStack) => slimStack?.smid
);
